<script>
export default {
  name: "DetailSection",
};
</script>

<script setup>
import { ref, unref, reactive, computed } from "vue";
import { format as formatDate, parseISO } from "date-fns";
import { datadogLogs } from "@datadog/browser-logs";
import { useVModel } from "@vueuse/core";
import {
  ClipboardIcon,
  ExclamationTriangleIcon,
} from "@heroicons/vue/24/outline";
import PersonCheckIcon from "@/components/svg-icons/PersonCheckIcon.vue";
import PersonCancelIcon from "@/components/svg-icons/PersonCancelIcon.vue";
import {
  CLASSES_EDUCATIONAL_FEE,
  MEDICAL_INSURANCE,
  SPAIN_MEDICAL_INSURANCE,
  entityTypes,
} from "@/components/program-manager/sessions/constants";
import {
  debounceSearchWrapper,
  useGetEntities,
} from "@/components/program-manager/sessions/composable";
import {
  ORDER_STATUS,
  ORDER_PRODUCT_STATUS,
  ERROR_TIMEOUT,
  SUCCESS_TIMEOUT,
  CANCELLATION_REASONS_OTHER,
} from "@/constants";
import {
  headers,
  ORDER_STATUSES,
  ORDER_CONTRACT_SAVE_ERROR_MSG,
  ORDER_CONTRACT_SAVE_SUCCESS_MSG,
  ORDER_CONTRACT_SAVE_SUCCESS_MSG_NO_INVOICE,
  AVAILABLE_UPDATE_ORDER_STATUS,
  MEETING_POINT_SECTION_KEYS,
} from "@/components/StudentFulfillment/components/OrderDetail/sections/Details/constants";
import Spinner from "@/components/helpers/Spinner.vue";
import VSelectCaret from "@/components/shared/select/VSelectCaret.vue";
import SearchableSelect from "@/components/shared/select/SearchableSelect.vue";
import BasicTextInput from "@/components/shared/input/BasicTextInput.vue";
import BaseButton from "@/components/shared/Button/BaseButton";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import ExpandableRows from "@/components/StudentFulfillment/components/OrderDetail/sections/Details/ExpandableRows.vue";
import ExpandableItem from "@/components/StudentFulfillment/components/OrderDetail/sections/Details/ExpandableItem.vue";
import HousingFulfillmentModal from "@/components/StudentFulfillment/components/modals/HousingFulfillmentModal";
import HousingFulfillmentModalAlt from "@/components/StudentFulfillment/components/modals/HousingFulfillmentModal/alternativeDesign.vue";
import AddonsFulfillmentModal from "@/components/StudentFulfillment/components/modals/AddonsFulfillmentModal";
import ExcursionsFulfillmentModal from "@/components/StudentFulfillment/components/modals/ExcursionsFulfillmentModal";
import ExcursionsFulfillmentModalAlt from "@/components/StudentFulfillment/components/modals/ExcursionsFulfillmentModal/alternativeDesign.vue";
import ClassesFeeFulfillmentModal from "@/components/StudentFulfillment/components/modals/ClassesFeeFulfillmentModal";
import ClassesFeeFulfillmentModalUi2 from "@/components/StudentFulfillment/components/modals/ClassesFeeFulfillmentModal/uiv2.vue";
import InsuranceFulfillmentModal from "@/components/StudentFulfillment/components/modals/InsuranceFulfillmentModal";
import InsuranceFulfillmentModalAlt from "@/components/StudentFulfillment/components/modals/InsuranceFulfillmentModal/alternativeDesign.vue";
import MeetingPointModal from "@/components/StudentFulfillment/components/modals/MeetingPointModal";
import { MEETING_POINT_KEYS } from "@/components/StudentFulfillment/components/modals/MeetingPointModal/constants";
import AddOnRequests from "@/components/StudentFulfillment/components/OrderDetail/sections/Details/AddOnRequests";
import { useAddOnRequestItems } from "@/components/StudentFulfillment/components/OrderDetail/sections/Details/AddOnRequests/composables/useAddOnRequestItems";
import { getTableData } from "@/components/StudentFulfillment/components/OrderDetail/sections/Details/utils/getTableFunctions";
import { useHousingQuestionnaire } from "@/composables/useHousingQuestionnaire";
import internshipService from "@/services/internship";
import {
  hasOrdersWriteFulfillPermission,
  hasItemsReadLitePermission,
  hasItemsReadPermission,
  hasItemsWritePermission,
  hasOrdersAdminPermission,
  hasOrdersWriteEvents,
  hasOrdersWriteMiscProducts,
  hasOrdersWriteInternships,
  hasOrdersWriteRooms,
  hasOrdersWriteAdminPermission,
  hasProgramsReadPermission,
  hasOrdersWritePermission,
  usePriceOverrides,
} from "@/composables/authorization";
import housingService from "@/services/housings";
import eventsService from "@/services/events";
import ordersService from "@/services/orders";
import profileService from "@/services/profile";
import { useQuery, useMutation } from "@tanstack/vue-query";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import {
  createColumnHelper,
  useVueTable,
  getCoreRowModel,
} from "@tanstack/vue-table";
import { PencilIcon } from "@heroicons/vue/24/outline";
import JSONtoHTML from "@/components/forms/SharedComponents/WYSIWYG/JSONtoHTML.vue";
import { hasMeetingPointData } from "@/components/StudentFulfillment/components/modals/MeetingPointModal/utils";

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => {},
  },
  programSession: {
    type: Object,
    default: () => {},
  },
  initialOrderStatus: {
    type: String,
    default: "",
  },
  hasInvoices: {
    type: Boolean,
    default: false,
  },
  sessionPrices: {
    type: Object,
    default: () => {},
  }
});

const itemNameRouteSetup = {
  "Academic: Study Session": {
    name: "edit-program-sessions",
    permission:
      hasItemsReadPermission.value ||
      hasItemsWritePermission.value ||
      hasProgramsReadPermission.value,
    extraParamName: "programSessionId",
  },
  Housing: {
    name: "housing-edit",
    permission:
      hasItemsReadPermission.value ||
      hasItemsWritePermission.value ||
      hasOrdersWriteRooms.value,
    extraParamName: "itemId",
  },
  "Academic: Internships": {
    name: "internship-item",
    permission:
      hasItemsReadPermission.value ||
      hasItemsWritePermission.value ||
      hasOrdersWriteInternships.value,
    extraParamName: "itemId",
  },
  "Academic: Excursions": {
    name: "edit-event",
    permission:
      hasItemsReadPermission.value || hasItemsReadLitePermission.value,
    extraParamName: "itemId",
  },
};

const toast = useToast();
const store = useStore();
const emit = defineEmits([
  "update:modelValue",
  "handleSaveButton",
  "withdrawOrder",
]);
const orderData = useVModel(props, "modelValue", emit, { passive: true });

const participantId = computed(() => orderData.value?.order?.participant_id);
const addOnRequestOrderId = computed(() => orderData.value?.order?.id);
const {
  items: addOnRequestItems,
  loading: addOnRequestItemsLoading,
  error: addOnRequestItemsError,
  refetch: refetchAddOnRequestItems,
  markAddedToOrder: markAddOnRequestItemAddedToOrder,
  markProductAddedToOrder: markAddOnRequestProductAddedToOrder,
} = useAddOnRequestItems(participantId, addOnRequestOrderId);

const selectedEntity = ref({});
const contractNumber = ref("");
const housingModalIsOpen = ref(false);
const isEditingOrderContract = ref(false);
const isSavingContract = ref(false);
const excursionsModalIsOpen = ref(false);
const addonsModalIsOpen = ref(false);
const insuranceModalIsOpen = ref(false);
const currentEditItemStatus = ref({});
const currentEditItems = ref([]);
const loadingExcursions = ref(false);
const classesFeeModalIsOpen = ref(false);
const currentEditItemClassesFee = ref({});
const sectionsExpandedStates = reactive({
  internships: false,
  housing: false,
  occurrences: false,
});

const orderStatusOptions = computed(
  () =>
    AVAILABLE_UPDATE_ORDER_STATUS?.[props?.initialOrderStatus] ||
    Object.values(ORDER_STATUS) ||
    []
);

const ROOMMATE_HOUSING_SECTION_KEY = "Roommate(s)";
const UNITMATE_HOUSING_SECTION_KEY = "Unitmate Requests";

const orderProductStatusOptions = computed(
  () => Object.values(ORDER_PRODUCT_STATUS) || []
);

const sessionId = computed(() => orderData?.value?.order?.session_id);
const internshipIds = computed(
  () => orderData?.value?.order?.internship_ids || []
);

const miscProductIds = computed(
  () =>
    orderData?.value?.product_details?.misc_products?.map(
      (misc) => misc?.misc_product?.id
    ) || []
);

const orderStatusNotFullfileed = computed(
  () => props?.initialOrderStatus !== ORDER_STATUSES.Fulfilled
);

const canEditOrderRoom = computed(
  () => orderStatusNotFullfileed.value && hasOrdersWriteRooms.value
);

const canEditOrderEvents = computed(
  () => orderStatusNotFullfileed.value && hasOrdersWriteEvents.value
);

const canEditOrderInternships = computed(
  () => orderStatusNotFullfileed.value && hasOrdersWriteEvents.value
);

const canEditOrderMiscProducts = computed(
  () => orderStatusNotFullfileed.value && hasOrdersWriteMiscProducts.value
);

const medicalInsuranceIndex = computed(() =>
  miscProductIds?.value?.findIndex(
    (item) =>
      item === MEDICAL_INSURANCE.id || item === SPAIN_MEDICAL_INSURANCE.id
  )
);

const classesFeesIndex = computed(() =>
  miscProductIds?.value?.indexOf(CLASSES_EDUCATIONAL_FEE.id)
);

const { getHousingQuestionnaire } = useHousingQuestionnaire();

const orderRoomHousingId = computed(
  () => orderData?.value?.product_details?.rooms?.[0]?.room?.unit?.housing?.id
);

const {
  execute: executeGetEntities,
  state: entitiesOptions,
  isLoading: entitiesOptionsLoading,
} = useGetEntities(
  { immediate: true, throwError: false, resetOnExecute: true },
  {
    extraParams: {
      account_types: [entityTypes.host_institution],
    },
  }
);

const fetchEntitiesOptions = debounceSearchWrapper(executeGetEntities, 250, {
  extraParams: {
    account_types: [entityTypes.home_institution, entityTypes.host_institution],
  },
});

const {
  isLoading: isLoadingMatchedMates,
  data: matchedMatesData,
  mutate: fetchMatchedMates,
} = useMutation({
  mutationFn: (mates) =>
    profileService.getMatchedMates({
      ...mates,
      email: orderData?.value?.order?.participant_email,
    }),
});

const {
  isLoading: isLoadingProfile,
  data: profileData,
  refetch: profileRefetch,
} = useQuery({
  queryKey: computed(() => ["fetchProfile", participantId.value]),
  queryFn: () =>
    profileService.getProfile(participantId.value),
});

const {
  isLoading: isLoadingHousingQuestionnaire,
  data: housingQuestionnaireData,
  refetch: housingQuestionnaireRefetch,
} = useQuery({
  queryKey: computed(() => ["fetchHousingQuestionnaire", orderData?.value?.order?.application_id]),
  queryFn: () => getHousingQuestionnaire(orderData?.value?.order?.application_id),
  enabled: sectionsExpandedStates.housing,
});


const {
  isLoading: isLoadingHousing,
  data: housingData,
  refetch: refetchHousing,
} = useQuery({
  queryKey: computed(() => ["fetchHousing", orderRoomHousingId.value]),
  queryFn: () => housingService.getHousingById(orderRoomHousingId.value, true),
  enabled: Boolean(orderRoomHousingId.value),
});

const {
  isLoading: isLoadingOrderInternships,
  data: orderInternshipsData,
  refetch: orderInternshipRefetch,
} = useQuery({
  queryKey: computed(() => ["fetchInternships", orderData?.value?.order?.internship_ids]),
  queryFn: () =>
    internshipService.getMany({
      internship_ids: orderData?.value?.order?.internship_ids,
    }),
  enabled: sectionsExpandedStates.internships,
});

const {
  isLoading: isLoadingOrderEventOccurrences,
  data: orderEventOccurrencesData,
  refetch: orderEventOccurrencesRefetch,
} = useQuery({
  queryKey: computed(() => ["fetchEventOccurrences", orderData?.value?.order?.event_occurrence_ids]),
  queryFn: () =>
    eventsService.getOccurrences({
      occurrence_ids: orderData?.value?.order?.event_occurrence_ids,
      limit: 25,
    }),
  enabled: sectionsExpandedStates.occurrences,
});

const closeHousingModal = async () => {
  housingModalIsOpen.value = false;
  refetchHousing();
};

const loadHousingQuestionnaire = async (isExpanded) => {
  if (isExpanded && !sectionsExpandedStates.housing) {
    sectionsExpandedStates.housing = true;
    await housingQuestionnaireRefetch();

    if (!_.isEmpty(housingSubmissionData?.value)) {
      fetchMatchedMates({
        requested_roommates:
          housingSubmissionData?.value?.requested_roommates?.map(
            (mate) => mate.email
          ) ?? [],
        requested_unitmates:
          housingSubmissionData?.value?.requested_housemates?.map(
            (mate) => mate.email
          ) ?? [],
      });
    }
  }
};

const handleSaveButton = async () => {
  if (isEditingOrderContract.value) saveOrderContract();
  if (props?.initialOrderStatus !== orderData.value.order.status)
    emit("handleSaveButton");
};

const loadInternships = (isExpanded) => {
  if (isExpanded && !sectionsExpandedStates.internships) {
    sectionsExpandedStates.internships = true;
    orderInternshipRefetch();
  }
};

const loadOccurrences = (isExpanded) => {
  if (isExpanded && !sectionsExpandedStates.occurrences) {
    sectionsExpandedStates.occurrences = true;
    orderEventOccurrencesRefetch();
  }
};

const getAnswer = (question) => {
  if (question?.answer) {
    return question?.answer;
  } else if (question?.answers) {
    return question?.answers.map((answer) => answer?.answer_text).join(", ");
  }
};

const entityOptionsFilter = (options) =>
  options.filter((option) => !!option.ope_id);

const orderInstitutionOptions = computed(
  () =>
    [
      ...entityOptionsFilter(entitiesOptions.value?.items || []),
      { ope_id: "dummy", id: null, name: "I dont know my school" },
    ] || []
);

const disableOrderContractButton = computed(
  () =>
    ![
      ORDER_STATUS.draft,
      ORDER_STATUS.paidConfirmed,
      ORDER_STATUS.submitted,
    ].includes(orderData.value?.order?.status) ||
    !hasOrdersWriteAdminPermission.value
);

const disableSaveButton = computed(() => {
  return (
    (!isEditingOrderContract.value &&
      props?.initialOrderStatus === orderData.value?.order?.status) ||
    orderData.value?.order?.status === ORDER_STATUS.withdrawn
  );
});

const isInstitutionUnsyncedWithProfile = computed(
  () =>
    profileData?.value?.data?.data?.colleges &&
    !!orderData.value?.order?.home_institution_id &&
    !profileData?.value?.data?.data?.colleges
      ?.map((item) => item?.college_id)
      ?.includes(orderData.value?.order?.home_institution_id)
);

const housingSubmissionData = computed(
  () => housingQuestionnaireData?.value?.submission_data || {}
);

const orderPaymentProcessing = computed(
  () => orderData?.value?.payment_processing
);

const questionaryQuestionsAndAnswers = computed(() => {
  let questionaryData = {};
  const questions = housingSubmissionData?.value?.question_responses;
  if (questions) {
    for (const question of questions) {
      questionaryData[question?.model] = getAnswer(question);
    }
  }
  return questionaryData;
});

const matchedRoommates = computed(
  () => matchedMatesData.value?.data?.roommates.map((mate) => mate.email) ?? []
);

const matchedUnitmates = computed(
  () => matchedMatesData.value?.data?.unitmates.map((mate) => mate.email) ?? []
);

const normalizedHousingQuestionnaire = computed(() => ({
  [ROOMMATE_HOUSING_SECTION_KEY]: housingSubmissionData?.value
    ?.requested_roommates?.length
    ? housingSubmissionData?.value?.requested_roommates
    : undefined,
  [UNITMATE_HOUSING_SECTION_KEY]: housingSubmissionData?.value
    ?.requested_housemates?.length
    ? housingSubmissionData?.value?.requested_housemates
    : undefined,
  Accomodation:
    questionaryQuestionsAndAnswers?.value?.housingRequirements ?? undefined,
  "Student Identifies As":
    profileData?.value?.data?.data?.gender_identity ?? undefined,
  "Student Requests":
    questionaryQuestionsAndAnswers?.value?.comfortableLivingIn ?? undefined,
  Smoking: questionaryQuestionsAndAnswers?.value?.smoking ?? undefined,
  Pets: questionaryQuestionsAndAnswers?.value?.livingWithPets ?? undefined,
  Meals:
    questionaryQuestionsAndAnswers?.value?.dietaryRestrictions ?? undefined,
  "Description of Self":
    questionaryQuestionsAndAnswers?.value?.describeMyself ?? undefined,
  "Additional Housing Request":
    questionaryQuestionsAndAnswers?.value?.additionalHousingRequest ??
    undefined,
}));

const columnHelper = createColumnHelper();

const table = useVueTable(
  reactive({
    get data() {
      return getTableData({
        orderPaymentProcessing,
        orderData,
        housingData,
        classesFeesIndex,
        orderInternshipsData,
        orderEventOccurrencesData,
        orderRoomHousingId,
        programSession: props.programSession,
      });
    },
    columns: headers.map((header) => {
      return columnHelper.accessor(header, {
        cell: (info) => info.getValue(),
        header,
      });
    }),
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  })
);

const sessionRow = computed(() => {
  return table.getRowModel()?.rows?.[0];
});

const housingRow = computed(() => {
  return table.getRowModel().rows?.[1];
});

const insuranceRow = computed(() => {
  return table.getRowModel().rows?.[2];
});

const classesFeesRow = computed(() => {
  return table.getRowModel().rows?.[3];
});

const internshipRows = computed(() => {
  return table
    .getRowModel()
    .rows.filter(
      (row) =>
        row.original.Type ===
        `Academic: Internships: ${orderData?.value?.product_details?.internships?.length}`
    );
});

const eventRows = computed(() => {
  return (
    table
      .getRowModel()
      .rows.filter(
        (row) =>
          row.original.Type ===
          `Academic: Excursions: ${orderData?.value?.product_details?.event_occurrences?.length}`
      ) || []
  );
});

const addonsRows = computed(() => {
  return (
    table
      .getRowModel()
      .rows.filter(
        (row) =>
          row.original.Type ===
          `Addons: ${orderData?.value?.product_details?.misc_products?.length}`
      ) || []
  );
});

const showWithdrawnReason = computed(() => {
  return (
    orderData?.value?.order?.status === ORDER_STATUS.withdrawn &&
    orderData?.value?.order?.withdrawn_reason
  );
});

const copyOrderId = (orderId) => {
  if (!orderId) return;
  navigator.clipboard.writeText(orderId);
};

const openStatusModal = (id, productType, objectKey, isAddon = true) => {
  currentEditItemStatus.value = orderData?.value?.product_details?.[
    productType
  ]?.find((item) => item?.[objectKey]?.id === id);
  if (currentEditItemStatus.value) {
    currentEditItemStatus.value.id = id;
  }
  if (isAddon) addonsModalIsOpen.value = true;
  else insuranceModalIsOpen.value = true;
};

const buildExcursionsData = async () => {
  loadingExcursions.value = true;
  if (orderData?.value?.order?.event_occurrence_ids.length) {
    await orderEventOccurrencesRefetch();
    currentEditItems.value =
      unref(orderEventOccurrencesData)?.data?.data?.items?.map((item) => {
        const orderEventData = unref(
          orderData
        )?.product_details?.event_occurrences?.find(
          (event) => event.id === item.id
        );


        // New Modals don't fallback to 0, they need to know if field is null to disable dropdowns
        // Retains old behavior if flag is off to avoid unintended bugs
        const item_price_in_cents = usePriceOverrides.value ? orderEventData?.total_price_in_cents : orderEventData?.total_price_in_cents || 0;

        return {
          ...item,
          new_product_id: item.id,
          name: item?.associated_event?.title,
          duration: `${item?.associated_event?.duration} ${item?.associated_event?.duration_units}`,
          title: orderEventData?.event_occurrence?.event_title || "",
          total_price_in_cents: item_price_in_cents,
          status: orderEventData?.status,
          quantity: orderEventData?.quantity,
          cancellation_reason: orderEventData?.cancellation_reason || "",
          latest_unapproved_price_override: orderEventData?.latest_unapproved_price_override || {},
          latest_approved_price_override: orderEventData?.price_override || {},
          price_before_fulfillment_in_cents: orderEventData?.price_before_fulfillment_in_cents,
        };
      }) || [];
  }
  loadingExcursions.value = false;
};

const openExcursionModal = () => {
  buildExcursionsData();
  excursionsModalIsOpen.value = true;
};

const updateSavedExcursionData = (dataResponse, item) => {
  if (item) {
    currentEditItems.value.push(item);
    // If this excursion was requested via an add-on request, reflect it as added
    // in the add-on request section.
    markAddOnRequestProductAddedToOrder(item.new_product_id);
  }
    orderData.value = dataResponse.data;

  orderEventOccurrencesRefetch();
};

const closeInsuranceModal = () => {
  currentEditItemStatus.value = {};
  insuranceModalIsOpen.value = false;
}

const closeStatusModal = (orderResponse, isAddon = true) => {
  if (orderResponse?.data?.order?.id) {
    orderData.value = orderResponse.data;
  }
  currentEditItemStatus.value = {};
  if (isAddon) addonsModalIsOpen.value = false;
  else insuranceModalIsOpen.value = false;
};

const closeExcursionsFulfillmentModal = () => {
  currentEditItems.value = [];
  excursionsModalIsOpen.value = false;
};

const openClassesFeeModal = (id, productType, objectKey) => {
  currentEditItemClassesFee.value = orderData?.value?.product_details?.[
    productType
  ]?.find((item) => item?.[objectKey]?.id === id);
  if (
    typeof currentEditItemClassesFee.value == "object" &&
    !!currentEditItemClassesFee.value
  ) {
    currentEditItemClassesFee.value.id = id;
    currentEditItemClassesFee.value.sessionPrice =
      props.programSession?.items?.class_academic_fees_price_in_cents ?? 0;
      currentEditItemClassesFee.value.latest_approved_price_override= currentEditItemClassesFee.value.price_override;
  }
  classesFeeModalIsOpen.value = true;
};

const closeClassesFeeModal = (orderResponse) => {
  if (orderResponse?.data?.order?.id) {
    orderData.value = orderResponse.data;
  }
  currentEditItemClassesFee.value = {};
  classesFeeModalIsOpen.value = false;
};

const toggleOrderContractFields = () => {
  isEditingOrderContract.value = !isEditingOrderContract.value;
  contractNumber.value = orderData.value?.order?.billing_contract_id;
  selectedEntity.value = {
    ope_id: orderData.value?.order?.ope_id,
    name: orderData.value?.order?.home_institution_name,
    id: orderData.value?.order?.home_institution_id,
  };
};

const saveOrderContract = async () => {
  try {
    const orderId = orderData.value?.order?.id;
    const homeInstititutionId = selectedEntity.value?.id;
    const formioResourceId = store?.state?.currentUser?.formioAdvisorId;
    let contractId = contractNumber.value;

    if (!homeInstititutionId) {
      contractId = null;
    }

    isSavingContract.value = true;

    if (formioResourceId) {
      const updatedOrder = await ordersService.updateOrderContract({
        orderId: orderId,
        payload: {
          home_institution_id: homeInstititutionId,
          billing_contract_id: contractId,
          formio_resource_id: formioResourceId,
        },
      });

      const contractUpdateSuccessMessage = computed(() => {
        return props?.hasInvoices
          ? ORDER_CONTRACT_SAVE_SUCCESS_MSG
          : ORDER_CONTRACT_SAVE_SUCCESS_MSG_NO_INVOICE;
      });
      if (updatedOrder) {
        orderData.value = updatedOrder?.data?.data;

        profileRefetch();

        toast.open({
          message: contractUpdateSuccessMessage.value,
          type: "success",
          position: "bottom",
          duration: SUCCESS_TIMEOUT,
        });
      } else {
        toast.open({
          message: ORDER_CONTRACT_SAVE_ERROR_MSG,
          type: "error",
          position: "bottom",
          duration: ERROR_TIMEOUT,
        });
      }
    } else {
      datadogLogs.logger.warn(
        "Attempting to update order contract but student has no formio id",
        {
          order: orderData?.value?.order || {},
          user: store?.state?.currentUser,
        }
      );
    }
  } catch (error) {
    datadogLogs.logger.error("Failed to update order contract", {
      error: error?.cause?.data?.details?.detail ?? undefined,
      order: orderData?.value?.order || {},
      user: store?.state?.currentUser,
    });
    toast.open({
      message: ORDER_CONTRACT_SAVE_ERROR_MSG,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  } finally {
    toggleOrderContractFields();
    isSavingContract.value = false;
  }
};

const handleUpdateStatus = (value) => {
  orderData.value.order.status = value;
  if (value === ORDER_STATUS.withdrawn) emit("withdrawOrder");
};

const editingEnabled = computed(() => isEditingOrderContract.value && hasOrdersWriteAdminPermission.value);

const hasMeetingPointInfo = computed(() => hasMeetingPointData(orderData.value?.order));
const meetingPointInformation = computed(() => ({
  [MEETING_POINT_SECTION_KEYS.meetingPointName]: orderData.value?.order?.meeting_point_name || "",
  [MEETING_POINT_SECTION_KEYS.meetingPointAddress]: orderData.value?.order?.meeting_point_address_line_1 || "",
  [MEETING_POINT_SECTION_KEYS.instructions]: orderData.value.order.meeting_point_instructions || "",
  [MEETING_POINT_SECTION_KEYS.edit]: "edit",
}));

const meetingPointModalState = ref(false);
const openMeetingPointModal = () => {
  meetingPointModalState.value = true;
};
const onMeetingPointModalUpdate = (orderResponse) => {
  if (orderResponse?.data?.order?.id) {
    orderData.value = orderResponse.data;
  }
};
</script>

<template>
  <MeetingPointModal
    v-model:visible="meetingPointModalState"
    :order="orderData"
    @on-update="onMeetingPointModalUpdate"
  />
  <HousingFulfillmentModal
    v-if="!usePriceOverrides && housingModalIsOpen"
    :order="orderData"
    :modal-is-open="housingModalIsOpen"
    @close-modal="closeHousingModal"
  />
  <HousingFulfillmentModalAlt
    v-else-if="usePriceOverrides && housingModalIsOpen"
    :order="orderData"
    :modal-is-open="housingModalIsOpen"
    :session-prices="props.sessionPrices"
    :program-session="programSession"
    @close-modal="closeHousingModal"
  />
  <InsuranceFulfillmentModal
    v-if="!usePriceOverrides && insuranceModalIsOpen"
    :order-id="orderData?.order?.id"
    :modal-is-open="insuranceModalIsOpen"
    :line-item="currentEditItemStatus"
    :profile-name="orderData?.order?.participant_fullname"
    :status-options="orderProductStatusOptions"
    @close-modal="closeStatusModal"
  />
  <InsuranceFulfillmentModalAlt
    v-else-if="usePriceOverrides && insuranceModalIsOpen"
    :order-id="orderData?.order?.id"
    :modal-is-open="insuranceModalIsOpen"
    :line-item="currentEditItemStatus"
    :profile-name="orderData?.order?.participant_fullname"
    :status-options="orderProductStatusOptions"
    :session-prices="props.sessionPrices"
    @close-modal="closeInsuranceModal"
  />

  <AddonsFulfillmentModal
    :order-id="orderData?.order?.id"
    :modal-is-open="addonsModalIsOpen"
    :status-options="orderProductStatusOptions"
    :session-id="sessionId"
    :profile-name="orderData?.order?.participant_fullname"
    :selected-misc-products="orderData?.product_details?.misc_products"
    @close-modal="closeStatusModal"
  />
  <ExcursionsFulfillmentModal
    v-if="!usePriceOverrides && excursionsModalIsOpen"
    :order-id="orderData?.order?.id"
    :modal-is-open="excursionsModalIsOpen"
    :profile-name="orderData?.order?.participant_fullname"
    :program-session="programSession"
    :excursions-data="currentEditItems"
    :loading-excursions="loadingExcursions"
    @update-saved-excursion-data="updateSavedExcursionData"
    @close-modal="closeExcursionsFulfillmentModal"
  />
  <ExcursionsFulfillmentModalAlt
    v-else-if="usePriceOverrides && excursionsModalIsOpen"
    :order-id="orderData?.order?.id"
    :modal-is-open="excursionsModalIsOpen"
    :profile-name="orderData?.order?.participant_fullname"
    :program-session="programSession"
    :excursions-data="currentEditItems"
    :loading-excursions="loadingExcursions"
    :session-prices="props.sessionPrices"
    @update-saved-excursion-data="updateSavedExcursionData"
    @close-modal="closeExcursionsFulfillmentModal"
  />
  <ClassesFeeFulfillmentModal
   v-if="!usePriceOverrides && classesFeeModalIsOpen"
    :order-id="orderData?.order?.id"
    :order-status="orderData?.order?.status"
    :modal-is-open="classesFeeModalIsOpen"
    :line-item="currentEditItemClassesFee"
    :status-options="orderProductStatusOptions"
    @close-modal="closeClassesFeeModal"
  />
  <ClassesFeeFulfillmentModalUi2
    v-else-if="usePriceOverrides && classesFeeModalIsOpen"
    :order-id="orderData?.order?.id"
    :order-status="orderData?.order?.status"
    :modal-is-open="classesFeeModalIsOpen"
    :line-item="currentEditItemClassesFee"
    :status-options="orderProductStatusOptions"
    :profile-name="orderData?.order?.participant_fullname"
    :session-prices="props.sessionPrices"
    @close-modal="closeClassesFeeModal"
  />
  <div
    class="pb-0 pt-4 px-4 space-y-2 space-x-4 flex flex-col bg-blue-200 md:pt-2 md:pb-4 md:items-center md:flex-row sm:justify-between"
  >
    <div
      class="group items-start flex justify-between md:flex-col md:justify-normal"
    >
      <span class="ml-2 font-semibold text-sm md:text-md md:ml-0">
        Order ID
      </span>
      <div class="flex">
        <span class="text-sm md:text-md">
          {{ orderData?.order?.id }}
        </span>
        <button
          class="text-sm md:text-md"
          @click="copyOrderId(orderData?.order?.id)"
        >
          <ClipboardIcon class="h-4 ml-2 mb-1 text-teal-900" />
        </button>
      </div>
    </div>
    <div
      v-if="orderData?.order?.created_at"
      class="group items-start flex justify-between md:flex-col md:justify-normal"
    >
      <span class="font-semibold text-sm md:text-md">
        Order Created Date
      </span>
      <span class="text-sm md:text-md">
        {{ formatDate(parseISO(orderData?.order?.created_at), "MM/dd/yyyy") }}
      </span>
    </div>
    <VSelectCaret
      v-if="orderData?.order?.status"
      :model-value="orderData.order.status"
      outer-classes="w-full checkbox-placeholder md:w-52"
      field-label="Order Status"
      :clearable="false"
      field-label-classes="font-semibold text-sm md:text-md"
      :options="orderStatusOptions"
      :disabled="!hasOrdersWriteFulfillPermission"
      @update:model-value="handleUpdateStatus"
    />
    <div
      v-if="hasOrdersWriteAdminPermission"
      class="hidden mx-4 self-stretch w-[3px] h-20 bg-blue-100 md:inline-block"
    ></div>
    <div v-if="hasOrdersWriteAdminPermission">
      <BasicTextInput
        v-if="editingEnabled"
        v-model="contractNumber"
        data-testid="update-contract-number-input"
        outer-classes="w-full checkbox-placeholder md:w-52 md:m-2"
        field-label-classes="font-semibold text-sm md:text-md"
        field-label="Contract Number"
        label="contract"
      />
      <div v-else>
        <div class="group items-start flex justify-between md:flex-col md:justify-normal">
          <span class="font-semibold text-sm md:text-md">
            Contract Number
          </span>
          <span class="text-sm md:text-md">
            {{ orderData?.order?.billing_contract_id || "No contract number" }}
          </span>
        </div>
      </div>
    </div>
    <div v-if="hasOrdersWriteAdminPermission">
      <SearchableSelect
        v-if="editingEnabled"
        v-model="selectedEntity"
        :filter="(options) => entityOptionsFilter(options)"
        :options="orderInstitutionOptions"
        :loading="entitiesOptionsLoading"
        :clear-search-on-blur="() => false"
        :deselect-from-dropdown="true"
        input-classes="w-full checkbox-placeholder md:w-52"
        field-label-classes="font-semibold text-sm md:text-md"
        placeholder="Search and select a institution"
        field-label="Home University"
        scroll-input="hidden"
        label="name"
        @search="fetchEntitiesOptions"
      />
      <div v-else>
        <div class="group items-start flex justify-between md:flex-col md:justify-normal">
          <span class="font-semibold text-sm md:text-md">
            Home University
          </span>
          <span class="flex items-center text-sm md:text-md">
            {{ orderData?.order?.home_institution_name || "No home institution selected" }}
            <button
              v-if="isInstitutionUnsyncedWithProfile && !isLoadingProfile"
              data-testid="update-contract-number-button"
              class="ml-2"
              data-tippy-content="The order institution is unsynced with the profile college."
            >
              <ExclamationTriangleIcon class="text-help-warning w-4 h-4" />
            </button>
          </span>
        </div>
      </div>
    </div>
    <div v-if="!isEditingOrderContract && hasOrdersWriteAdminPermission">
      <BaseButton
        class="w-full text-sm h-10 mr-0 md:w-auto md:text-md"
        :disabled="disableOrderContractButton"
        data-test-id="change-order-contract-button"
        label="Change Contract"
        @click="toggleOrderContractFields"
      />
    </div>
    <div v-else-if="isEditingOrderContract && hasOrdersWriteAdminPermission" class="flex">
      <BaseButton
        class="w-full text-sm h-10 mr-0 md:w-auto md:text-md"
        data-test-id="cancel-order-contract-button"
        label="Cancel"
        outlined
        @click="toggleOrderContractFields"
      />
    </div>
    <ButtonWithSpinner
      class="w-full text-sm h-10 md:w-auto md:text-md md:w-28 disabled:!h-10"
      type="submit"
      data-test-id="save-order-contract-button"
      variant="secondary"
      variant-type="normal"
      :disabled="disableSaveButton"
      :prop-loading="isSavingContract"
      @click="handleSaveButton"
    >
      <span>
        Save
      </span>
    </ButtonWithSpinner>
  </div>
  <div
    v-if="showWithdrawnReason"
    class="px-4 flex bg-blue-200 sm:justify-between"
  >
    <div class="w-full my-2 pb-4">
      <span class="font-semibold text-sm md:text-md"
        >Reason for withdrawal:
      </span>
      <span class="text-sm md:text-md">
        {{
          orderData?.order?.withdrawn_reason === CANCELLATION_REASONS_OTHER &&
          orderData?.order?.withdrawn_reason_other
            ? orderData?.order?.withdrawn_reason_other
            : orderData?.order?.withdrawn_reason
        }}</span
      >
    </div>
  </div>

  <div class="px-4">
    <AddOnRequests
      :order-id="orderData?.order?.id"
      :items="addOnRequestItems"
      :loading="addOnRequestItemsLoading"
      :error="addOnRequestItemsError"
      @order-updated="updateSavedExcursionData"
      @retry="refetchAddOnRequestItems"
      @item-added="markAddOnRequestItemAddedToOrder"
    />
  </div>

  <div class="overflow-x-auto md:overflow-x-hidden">
    <table v-if="!isLoadingHousing && table" class="m-4 p-2 w-full">
      <thead>
        <tr
          v-for="headerGroup in table.getHeaderGroups()"
          :key="headerGroup.id"
        >
          <th
            v-for="header in headerGroup.headers"
            :key="header.id"
            class="py-4 pr-3 first:pl-2 text-left text-sm items-center text-gray-900"
          >
            <div class="flex items-center">
              {{ header.column.columnDef.header }}
            </div>
          </th>
        </tr>
      </thead>

      <tbody class="bg-white">
        <ExpandableItem
          v-if="sessionId"
          label="Academic: Study Session"
          :row="sessionRow"
          :expand-enabled="hasMeetingPointInfo"
          :edit-enabled="false"
          :item-name-route-setup="itemNameRouteSetup"
          :data="meetingPointInformation"
          expand-content-class="grid grid-cols-4 gap-4"
        >
          <template #[MEETING_POINT_SECTION_KEYS.instructions]="{ value }">
            <span class="uppercase text-gray-900 text-md font-semibold">
              {{ MEETING_POINT_SECTION_KEYS.instructions }}
            </span>
            <JSONtoHTML class="" :tiptap-json="value" />
          </template>
          <template #[MEETING_POINT_SECTION_KEYS.edit]>
            <button
              :disabled="!hasOrdersWritePermission"
              class="flex justify-end items-end mr-2 2xl:mr-6 text-md font-bold bg-transparent text-teal-900"
              @click="openMeetingPointModal"
            >
              <PencilIcon class="h-5" />
            </button>
          </template>
        </ExpandableItem>

        <ExpandableItem
          v-if="classesFeesIndex >= 0"
          label="Academics: Classes"
          :row="classesFeesRow"
          :expand-enabled="false"
          :status-options="orderProductStatusOptions"
          :edit-enabled="canEditOrderMiscProducts"
          :item-name-route-setup="itemNameRouteSetup"
          @open-status-modal="
            (id) => openClassesFeeModal(id, 'misc_products', 'misc_product')
          "
        />

        <ExpandableRows
          v-if="internshipIds.length"
          label="Academic: Internships"
          :rows="internshipRows"
          :loading="isLoadingOrderInternships"
          :edit-enabled="canEditOrderInternships"
          :item-name-route-setup="itemNameRouteSetup"
          @expanded="loadInternships"
        />
        <ExpandableItem
          label="Housing"
          :row="housingRow"
          :data="normalizedHousingQuestionnaire"
          :loading="
            isLoadingHousingQuestionnaire ||
            isLoadingProfile ||
            isLoadingMatchedMates
          "
          :edit-enabled="canEditOrderRoom"
          :item-name-route-setup="itemNameRouteSetup"
          @open-status-modal="() => (housingModalIsOpen = true)"
          @expanded="loadHousingQuestionnaire"
        >
          <template #[ROOMMATE_HOUSING_SECTION_KEY]="{ value }">
            <div v-if="!!value.length">
              <span class="uppercase text-gray-900 text-md font-semibold">
                {{ ROOMMATE_HOUSING_SECTION_KEY }}
              </span>
              <div v-for="mate in value ?? []" class="flex space-x-2">
                <span>
                  {{ mate?.first_name ?? "" }} {{ mate?.last_name ?? "" }}</span
                >
                <PersonCheckIcon
                  v-if="matchedRoommates.includes(mate.email)"
                  fill="#007f80"
                />
                <PersonCancelIcon v-else fill="#7f1d1d" />
              </div>
            </div>
          </template>
          <template #[UNITMATE_HOUSING_SECTION_KEY]="{ value }">
            <div v-if="!!value.length">
              <span class="uppercase text-gray-900 text-md font-semibold">
                {{ UNITMATE_HOUSING_SECTION_KEY }}
              </span>
              <div v-for="mate in value ?? []" class="flex space-x-2">
                <span>
                  {{ mate?.first_name ?? "" }} {{ mate?.last_name ?? "" }}</span
                >
                <PersonCheckIcon
                  v-if="matchedUnitmates.includes(mate.email)"
                  fill="#007f80"
                />
                <PersonCancelIcon v-else fill="#7f1d1d" />
              </div>
            </div>
          </template>
        </ExpandableItem>

        <ExpandableRows
          label="Academic: Excursions"
          :rows="eventRows"
          :loading="isLoadingOrderEventOccurrences"
          :edit-enabled="canEditOrderEvents"
          :item-name-route-setup="itemNameRouteSetup"
          @expanded="loadOccurrences"
          @open-status-modal="openExcursionModal"
        />

        <ExpandableItem
          v-if="medicalInsuranceIndex >= 0"
          label="Insurance"
          :row="insuranceRow"
          :expand-enabled="false"
          :status-options="orderProductStatusOptions"
          :edit-enabled="canEditOrderMiscProducts"
          :item-name-route-setup="itemNameRouteSetup"
          @open-status-modal="
            (id) => openStatusModal(id, 'misc_products', 'misc_product', false)
          "
        />

        <ExpandableRows
          label="Add Ons"
          :rows="addonsRows"
          :edit-enabled="canEditOrderMiscProducts"
          :item-name-route-setup="itemNameRouteSetup"
          @open-status-modal="
            (id) => {
              openStatusModal(id, 'misc_products', 'misc_product');
            }
          "
        />
      </tbody>
    </table>
    <Spinner v-else class="p-2 relative bg-blue-100" />
  </div>
</template>
